import { DateTime } from "luxon";
import { FleetManagementRow, VehicleBookingEventDto } from "../../types/FleetManagement-row.types";
import { VehicleBlockingEvent } from "../../types/FleetManagement.types";
import { FleetCalendarEvent } from "../FleetManagementEventDetailDialog.component";
import {
  buildEventsForAllFleet,
  buildEventsForSingleVehicle
} from "../functions/buildFleetCalendarEvents.function";
import { useEffect, useRef, useState } from "react";

export function useShouldResetFleetEvents(
  isLoadingEvents: boolean,
  showAllEvents: boolean,
  stableAllFleetData?: FleetManagementRow[],
  selectedRow?: FleetManagementRow | null
) {
  return (
    isLoadingEvents ||
    (showAllEvents && !stableAllFleetData?.length) ||
    (!showAllEvents && !selectedRow)
  );
}

export function buildFleetEvents({
  showAllEvents,
  stableAllFleetData,
  selectedRow,
  bookingEvents,
  blockingEvents,
  timezone,
  isEditable,
  bookingDateRange,
  t
}: {
  showAllEvents: boolean;
  stableAllFleetData?: FleetManagementRow[];
  selectedRow?: FleetManagementRow | null;
  bookingEvents?: VehicleBookingEventDto[];
  blockingEvents?: VehicleBlockingEvent[];
  timezone: string;
  isEditable: boolean;
  bookingDateRange?: { start: DateTime; end: DateTime };
  t: (key: string) => string;
}): FleetCalendarEvent[] {
  const events = showAllEvents
    ? buildEventsForAllFleet(stableAllFleetData!, bookingEvents, blockingEvents ?? [], timezone)
    : buildEventsForSingleVehicle(selectedRow!, bookingEvents, blockingEvents ?? [], timezone);

  if (!isEditable && bookingDateRange) {
    const selStart = bookingDateRange.start.setZone(timezone);
    const selEnd = bookingDateRange.end.setZone(timezone);

    if (selStart.isValid && selEnd.isValid) {
      events.push({
        id: "selected-date-range",
        title: t("Selected Dates"),
        start: selStart.toISO(),
        end: selEnd.toISO(),
        color: "#4CAF50",
        editable: false
      });
    }
  }

  return events;
}

export function areEventsEqual(prev: FleetCalendarEvent[], next: FleetCalendarEvent[]) {
  return prev.length === next.length && prev.every((e, i) => e.id === next[i]?.id);
}

type Args = {
  isLoadingEvents: boolean;
  showAllEvents: boolean;
  stableAllFleetData?: FleetManagementRow[];
  selectedRow?: FleetManagementRow | null;
  bookingEvents?: VehicleBookingEventDto[];
  blockingEvents?: VehicleBlockingEvent[];
  timezone: string;
  isEditable: boolean;
  bookingDateRange?: { start: DateTime; end: DateTime };
  t: (key: string) => string;
};

export function useFleetEventsState(args: Readonly<Args>) {
  const {
    isLoadingEvents,
    showAllEvents,
    stableAllFleetData,
    selectedRow,
    bookingEvents,
    blockingEvents,
    timezone,
    isEditable,
    bookingDateRange,
    t
  } = args;

  const [fleetEvents, setFleetEvents] = useState<FleetCalendarEvent[]>([]);

  const prevEventsRef = useRef<FleetCalendarEvent[]>([]);

  const shouldReset = useShouldResetFleetEvents(
    isLoadingEvents,
    showAllEvents,
    stableAllFleetData,
    selectedRow
  );

  useEffect(() => {
    if (shouldReset) {
      if (prevEventsRef.current.length) {
        prevEventsRef.current = [];
        setFleetEvents([]);
      }
      return;
    }

    const events = buildFleetEvents({
      showAllEvents,
      stableAllFleetData,
      selectedRow,
      bookingEvents,
      blockingEvents,
      timezone,
      isEditable,
      bookingDateRange,
      t
    });

    // Always set so fleetEvents stays in sync after refetch (areEventsEqual only compared ids,
    // so updates to same booking/block were skipped and calendar showed stale data).
    prevEventsRef.current = events;
    setFleetEvents(events);
  }, [
    isLoadingEvents,
    shouldReset,
    showAllEvents,
    stableAllFleetData,
    selectedRow,
    bookingEvents,
    blockingEvents,
    timezone,
    isEditable,
    bookingDateRange,
    t
  ]);

  return { fleetEvents, setFleetEvents };
}
